export type GuardedMethod = 'removeChild' | 'insertBefore';

/** Set on the patched methods so a hot reload does not wrap them again. */
const GUARD_MARK = '__translationGuard';

/**
 * Keep React alive under Chrome/Edge page translation.
 *
 * Translation swaps text nodes for `<font>` wrappers. React still holds
 * the old text node, so its next update calls `removeChild` or
 * `insertBefore` with a node that is no longer there and the browser
 * throws `NotFoundError`. While the page is translated, such a call is
 * skipped and `onSkip` runs once per method. Otherwise it throws as
 * usual, so real DOM bugs still surface.
 *
 * See https://github.com/facebook/react/issues/11538.
 */
export const installTranslationGuard = (
  onSkip: (method: GuardedMethod) => void
): void => {
  if (typeof Node !== 'function' || !Node.prototype) return;
  if (GUARD_MARK in Node.prototype.removeChild) return;

  // Chrome and Edge tag the root element while a translation is shown.
  const isTranslated = () => {
    const classes = document.documentElement.classList;
    return (
      classes.contains('translated-ltr') || classes.contains('translated-rtl')
    );
  };

  const reported = new Set<GuardedMethod>();
  const skip = (method: GuardedMethod) => {
    if (reported.has(method)) return;
    reported.add(method);
    onSkip(method);
  };

  const originalRemoveChild = Node.prototype.removeChild;
  const removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this && isTranslated()) {
      skip('removeChild');
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  const insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this && isTranslated()) {
      skip('insertBefore');
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };

  Object.defineProperty(removeChild, GUARD_MARK, { value: true });
  Node.prototype.removeChild = removeChild;
  Node.prototype.insertBefore = insertBefore;
};
